import React, { forwardRef, ReactNode } from "react";

import styled from "styled-components/macro";
import { theme } from "ui/theme";
import assertExhausted from "helpers/assertExhausted";

import "./fonts/fonts.css";

export type TypographySize =
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "subheading"
  | "bodySansSerif"
  | "bodySerif"
  | "meta"
  | "tooltip";

export type TypographyColor =
  | "ColorWhite"
  | "ColorSpace50"
  | "ColorSpace100"
  | "ColorSpace300"
  | "ColorSpace400"
  | "ColorSpace500"
  | "ColorSneaker100" /* Has been replaced by ColorSpace50 in design and will be removed in the future */
  | "ColorSneaker300" /* Has been replaced by ColorSpace300 in design and will be removed in the future */
  | "ColorEraserRed200"
  | "ColorBlurple500";

export type HeaderSize = "h1" | "h2" | "h3" | "h4";

type TypographyAlignment = "left" | "right" | "center" | "justify" | "inherit";

type TypographyElement = "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "span" | "p";

type TypographyFontStyle = "normal" | "italic";

export type TypographyFontWeight = "normal" | "bold" | "light";

interface BaseTypographyProps {
  color?: TypographyColor;
  fontStyle?: TypographyFontStyle;
  fontWeight?: TypographyFontWeight;
  textAlign: TypographyAlignment;
}

const baseProps = ["color", "fontStyle", "fontWeight", "textAlign"];

const WEIGHTS = {
  light: theme.FontWeightLight,
  normal: theme.FontWeightNormal,
  bold: theme.FontWeightBold,
};

const typography = styled.span.withConfig({
  shouldForwardProp: (prop) => !baseProps.includes(prop),
})<BaseTypographyProps>`
  color: ${({ color }) => theme[color || "ColorSpace500"]};
  ${({ fontStyle }) => fontStyle && `font-style: ${fontStyle};`}
  ${({ fontWeight }) => fontWeight && `font-weight: ${WEIGHTS[fontWeight]};`}
  margin-bottom: 0;
  text-align: ${(props) => props.textAlign};
`;

const Heading = styled(typography)`
  font-family: ${theme.FontFamilyHeading};
`;

const H1 = styled(Heading)`
  font-size: ${theme.FontSizeH1};
  line-height: ${theme.FontLineHeightH1};
`;

const H2 = styled(Heading)`
  font-size: ${theme.FontSizeH2};
  line-height: ${theme.FontLineHeightH2};
`;

const H3 = styled(Heading).attrs((props: BaseTypographyProps) => ({
  fontWeight: props.fontWeight || "normal",
}))`
  font-size: ${theme.FontSizeH3};
  line-height: ${theme.FontLineHeightH3};
`;

const H4 = styled(Heading)`
  font-size: ${theme.FontSizeH4};
  line-height: ${theme.FontLineHeightH4};
`;

const SubHeading = styled(typography).attrs((props: BaseTypographyProps) => ({
  fontWeight: props.fontWeight || "bold",
}))`
  font-family: ${theme.FontFamilySubHeading};
  font-size: ${theme.FontSizeSubHeading};
  line-height: ${theme.FontLineHeightSubHeading};
`;

const BodySansSerif = styled(typography)`
  font-family: ${theme.FontFamilyBodySansSerif};
  font-size: ${theme.FontSizeBody};
  line-height: ${theme.FontLineHeightBody};
`;

const BodySerif = styled(typography)`
  font-family: ${theme.FontFamilyBodySerif};
  font-size: ${theme.FontSizeBody};
  line-height: ${theme.FontLineHeightBody};
`;

const Meta = styled(typography)`
  font-family: ${theme.FontFamilyMeta};
  font-size: ${theme.FontSizeBody};
  line-height: ${theme.FontLineHeightBody};
`;

const Tooltip = styled(typography).attrs((props: BaseTypographyProps) => ({
  color: props.color || "ColorSneaker100",
}))`
  font-family: ${theme.FontFamilyTooltip};
  font-size: ${theme.FontSizeTooltip};
  line-height: ${theme.FontLineHeightTooltip};
`;

function elementFromSize(size: TypographySize): TypographyElement {
  switch (size) {
    case "h1":
    case "h2":
    case "h3":
    case "h4":
      return size;
    case "subheading":
    case "bodySansSerif":
    case "bodySerif":
    case "meta":
    case "tooltip":
      return "p";
    default:
      throw assertExhausted(size);
  }
}

function styledComponentFromSize(size: TypographySize): typeof typography {
  switch (size) {
    case "h1":
      return H1;
    case "h2":
      return H2;
    case "h3":
      return H3;
    case "h4":
      return H4;
    case "subheading":
      return SubHeading;
    case "bodySansSerif":
      return BodySansSerif;
    case "bodySerif":
      return BodySerif;
    case "meta":
      return Meta;
    case "tooltip":
      return Tooltip;
    default:
      throw assertExhausted(size);
  }
}

export type TypographyProps = {
  /** Optional ID for the HTML element */
  id?: string;
  /**
   * This prop can be used to specify which heading element should
   * actually be rendered, in the case that you want to render an element
   * as one heading but style it as if it were another. If both an `as` prop
   * and a `size` prop are passed into an invocation, the `as` will be used
   * to determine the element and the `size` will be used to determine the
   * classname (See example 1 below)
   */
  as?: TypographyElement;
  /**
   * The content that should be present in the heading.
   */
  children?: ReactNode;
  /**
   * Custom CSS class
   */
  className?: string;
  /**
   * The size of the html heading element. If no `as` prop is provided, then
   * the component uses this value to determine which html tag to render
   * (e.g. 'h1', 'h2', etc.)
   */
  size: TypographySize;
  /**
   * The color of the html heading element. If no color provided, a default color
   * will be used.
   */
  color?: TypographyColor;
  /**
   * Text alignment
   */
  textAlign?: TypographyAlignment;
  /**
   * The font-style of the html element, either "normal" or "italic".
   */
  fontStyle?: TypographyFontStyle;
  /**
   * The font-weight of the html element. If no font-weight is provided, a font-weight
   * will be used.
   */
  fontWeight?: TypographyFontWeight;
  /**
   * Allow the element to be focusable.
   * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/tabindex
   */
  tabIndex?: number;
  title?: string;
};

const Typography = forwardRef<HTMLElement, TypographyProps>((props, ref) => {
  const {
    as,
    size,
    className,
    color,
    children,
    textAlign = "inherit",
    fontStyle,
    fontWeight,
    /**
     * Components that wrap typography sometimes requires props such as event handlers
     * to be passed down into the element. One example is the tooltip component.  It
     * attaches a onHover and onFocus event to the element to determine when to
     * trigger the overlay.
     */
    ...rest
  } = props;
  const TypographyComponent = styledComponentFromSize(size);

  return (
    <TypographyComponent
      as={as || elementFromSize(size)}
      className={className}
      color={color}
      textAlign={textAlign}
      fontStyle={fontStyle}
      fontWeight={fontWeight}
      ref={ref}
      {...rest}
    >
      {children}
    </TypographyComponent>
  );
});

Typography.displayName = "Typography";

export default Typography;
